import { Children, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { admins, baseURL, currentSeason, divisionMappings, WRAPPED_PUBLIC, WRAPPED_SEASON } from "../../Constants";
import BetaBanner from "../../components/BetaBanner";
import { AuthContext } from "../../context/AuthContext";
import Loading from "../../components/Loading";
import {
  WrappedResponse,
  WrappedClip,
  getWrapped,
  trackWrapped,
  wrappedPhotoUrl,
} from "../../services/WrappedService";
import WrappedChart from "./WrappedChart";
import WrappedAreaMap from "./WrappedAreaMap";
import WrappedPositionMap from "./WrappedPositionMap";
import { track } from "../../utils/track";
import { parseMatchTime } from "../../utils/matchTime";
import styles from "./Wrapped.module.css";

// Full-bleed slide backgrounds — the loading-screen style map photos already in the bundle.
import bgAncient from "../../content/maps/de_ancient.jpg";
import bgAnubis from "../../content/maps/de_anubis.jpg";
import bgDust2 from "../../content/maps/de_dust2.jpg";
import bgInferno from "../../content/maps/de_inferno.jpg";
import bgMirage from "../../content/maps/de_mirage.jpg";
import bgNuke from "../../content/maps/de_nuke.jpg";
import bgOverpass from "../../content/maps/de_overpass.jpg";
import bgTrain from "../../content/maps/de_train.jpg";
import bgVertigo from "../../content/maps/de_vertigo.jpg";
import bgCache from "../../content/maps/de_cache.jpg";

const mapBackgrounds: Record<string, string> = {
  de_ancient: bgAncient,
  de_anubis: bgAnubis,
  de_dust2: bgDust2,
  de_inferno: bgInferno,
  de_mirage: bgMirage,
  de_nuke: bgNuke,
  de_overpass: bgOverpass,
  de_train: bgTrain,
  de_vertigo: bgVertigo,
  de_cache: bgCache,
};

// ---------- helpers ----------

/** "Top 4%" framing: PERCENT_RANK 0..1 (higher = better) → percent from the top. */
const topPct = (p: number): number => Math.max(1, Math.round((1 - p) * 100));

/** Roast direction: how many the player "beat" (e.g. more blind than X% of the league). */
const morePct = (p: number): number => Math.max(1, Math.round(p * 100));

const minutes = (seconds: number): number => Math.round(seconds / 60);

/** Raw division key → full display name: "3divC" → "3. Division C" (same convention as LFT 2.0). */
const fmtDivision = (name?: string): string | undefined => {
  if (!name) return undefined;
  const mapped = divisionMappings.get(name) ?? name;
  return mapped.replace(/^(\d) /, "$1. ");
};

/** "de_nuke" → "Nuke" for display. */
const fmtMap = (name?: string): string =>
  name ? name.replace(/^de_/, "").replace(/^\w/, (c) => c.toUpperCase()) : "?";

/** Percentile badges are flexes — only show one when it actually brags (top half or better). */
const topPctBadge = (t: (key: string, opts?: Record<string, unknown>) => string, p?: number): string | undefined =>
  p !== undefined && topPct(p) <= 50 ? t("wrapped.flex.topPct", { pct: topPct(p) }) : undefined;

// ---------- animated number ----------

const useCountUp = (target: number, durationMs = 1100): number => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

const CountUp = ({ value, suffix = "", decimals = 0 }: { value: number; suffix?: string; decimals?: number }) => {
  // Animate scaled integers so decimals (rating 0.94) still count up smoothly.
  const factor = Math.pow(10, decimals);
  const animated = useCountUp(Math.round(value * factor));
  return <>{(animated / factor).toFixed(decimals)}{suffix}</>;
};

// ---------- slide building blocks ----------

const Tile = ({ value, label, badge, tone, decimals, suffix }: {
  value: number | string;
  label: string;
  badge?: string;
  tone?: "accent" | "good" | "bad";
  decimals?: number;
  suffix?: string;
}) => (
  <div className={styles.tile}>
    <div className={`${styles.tileValue} ${tone === "accent" ? styles.tileValueAccent : tone === "good" ? styles.tileValueGood : tone === "bad" ? styles.tileValueBad : ""}`}>
      {typeof value === "number" ? <CountUp value={value} decimals={decimals ?? 0} suffix={suffix} /> : value}
    </div>
    <div className={styles.tileLabel}>{label}</div>
    {badge && <div className={styles.pctBadge}>{badge}</div>}
  </div>
);

// Viewport hook — drives the JS-side responsive bits (tile column count, mobile bottom bar logic).
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 640);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth <= 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
};

// Design-matched stat grid (Wrapped.dc.html): borderless values with thin vertical dividers
// between columns — NOT boxed cards. Column count collapses on mobile (>=3 → 2).
const Tiles = ({ cols, mobile, children }: { cols: number; mobile: boolean; children: React.ReactNode }) => {
  const n = mobile ? (cols >= 3 ? 2 : cols) : cols;
  const items = Children.toArray(children);
  return (
    <div
      className={styles.tiles}
      style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, maxWidth: cols <= 3 ? 620 : 900 }}
    >
      {items.map((child, i) => (
        <div key={i} className={styles.tileCell} style={i % n === 0 ? undefined : { borderLeft: "1px solid rgba(255,255,255,0.1)" }}>
          {child}
        </div>
      ))}
    </div>
  );
};

// ---------- benchmarking (the FACEIT "was I good?" layer) ----------

/**
 * Six verdict tiers instead of FACEIT's two. They grade against a global pool so almost
 * everyone reads "OKAY"; we grade against the player's own division first, which is both
 * kinder and more actionable. `inverted` stats (time spent blind) are roasted, not failed.
 */
type VerdictTone = "elite" | "strong" | "solid" | "ok" | "under" | "roast";

const verdictTone = (pct: number, inverted?: boolean): VerdictTone => {
  if (inverted) return pct >= 0.5 ? "roast" : "solid";
  if (pct >= 0.9) return "elite";
  if (pct >= 0.75) return "strong";
  if (pct >= 0.55) return "solid";
  if (pct >= 0.35) return "ok";
  return "under";
};

/**
 * One benchmarked stat: value, a percentile track with a tick, and a verdict — shown in
 * BOTH directions. The old `topPctBadge` only rendered when the number flattered
 * (top half or better), which hid the benchmark exactly when it was most useful.
 */
const StatBar = ({ label, display, pct, divisionPct, division, inverted }: {
  label: string;
  display: string;
  /** League percentile, 0..1. */
  pct: number;
  /** Division percentile when the pool was big enough (MIN_DIVISION_POOL on the server). */
  divisionPct?: number;
  division?: string;
  inverted?: boolean;
}) => {
  const { t } = useTranslation("pages");
  // Grade on the division when we have it — that's the pool the player actually competes in.
  const gradePct = divisionPct ?? pct;
  const tone = verdictTone(gradePct, inverted);
  const fill = Math.min(100, Math.max(0, gradePct * 100));

  // "Top 89%" is nonsense for a below-median player — it reads like a boast and means the
  // opposite. Above the median we brag with "Top X%"; below it we state the plain truth,
  // "better than X%", which is honest and still actionable.
  const phrase = (p: number, divName?: string) => {
    const above = p >= 0.5;
    const key = above
      ? (divName ? "bench.topIn" : "bench.topLeague")
      : (divName ? "bench.betterIn" : "bench.betterLeague");
    return t(`wrapped.${key}`, { pct: above ? topPct(p) : morePct(p), div: divName });
  };

  const context = inverted
    ? t("wrapped.bench.moreThan", { pct: morePct(pct) })
    : divisionPct !== undefined && division
      ? `${phrase(divisionPct, division)} · ${phrase(pct)}`
      : phrase(pct);

  return (
    <div className={styles.statBar}>
      <div className={styles.statBarHead}>
        <span className={styles.statBarLabel}>{label}</span>
        <span className={styles.statBarValue}>{display}</span>
      </div>
      <div className={styles.statBarTrack}>
        <span className={`${styles.statBarFill} ${styles[`sbFill_${tone}`]}`} style={{ width: `${fill}%` }} />
        <span className={styles.statBarTick} style={{ left: `${fill}%` }} />
      </div>
      <div className={styles.statBarFoot}>
        <span className={styles.statBarCtx}>{context}</span>
        <span className={`${styles.statBarVerdict} ${styles[`sbTone_${tone}`]}`}>
          {t(`wrapped.bench.tone.${tone}`)}
        </span>
      </div>
    </div>
  );
};

const Bar = ({ label, pct, value, delay }: { label: string; pct: number; value: string; delay: number }) => (
  <div className={styles.barRow}>
    <div className={styles.barLabel}>{label}</div>
    <div className={styles.barTrack}>
      <div className={styles.barFill} style={{ width: `${Math.min(100, pct * 100)}%`, animationDelay: `${delay}ms` }} />
    </div>
    <div className={styles.barValue}>{value}</div>
  </div>
);

// Charts live in WrappedChart.tsx — real axes, tooltips, W/L shape encoding.

/**
 * One benchmarked row on the share card's report-card variant: value, a thin percentile
 * track and the verdict word. Same grading as StatBar, compressed for a 8:5 card.
 */
const CardBar = ({ label, display, pct, divisionPct, inverted }: {
  label: string;
  display: string;
  pct: number;
  divisionPct?: number;
  inverted?: boolean;
}) => {
  const { t } = useTranslation("pages");
  const gradePct = divisionPct ?? pct;
  const tone = verdictTone(gradePct, inverted);
  return (
    <div className={styles.cardBar}>
      <div className={styles.cardBarTop}>
        <span className={styles.cardBarLabel}>{label}</span>
        <span className={styles.cardBarValue}>{display}</span>
      </div>
      <div className={styles.cardBarTrack}>
        <span
          className={`${styles.cardBarFill} ${styles[`sbFill_${tone}`]}`}
          style={{ width: `${Math.min(100, Math.max(0, gradePct * 100))}%` }}
        />
      </div>
      <div className={`${styles.cardBarVerdict} ${styles[`sbTone_${tone}`]}`}>
        {t(`wrapped.bench.tone.${tone}`)}
      </div>
    </div>
  );
};

/** A bordered stat tile in the season card's right-hand grid (FACEIT's tile treatment). */
const CardTile = ({ value, label, tone }: {
  value: string | number;
  label: string;
  tone?: "good" | "bad";
}) => (
  <div className={styles.cardTile}>
    <div className={`${styles.cardTileValue} ${tone === "good" ? styles.cardTileGood : tone === "bad" ? styles.cardTileBad : ""}`}>
      {value}
    </div>
    <div className={styles.cardTileLabel}>{label}</div>
  </div>
);

const PlayerHero = ({ steamId, name, score, sub, good }: {
  steamId: string;
  name?: string;
  score: string;
  sub: string;
  good?: boolean;
}) => (
  <div className={`${styles.calloutHero} ${good ? styles.calloutHeroGood : ""}`}>
    <img className={styles.calloutHeroPhoto} src={wrappedPhotoUrl(steamId, 280)} alt="" />
    <div style={{ textAlign: "left" }}>
      <div className={styles.calloutHeroName}>
        <Link to={`/player/${steamId}`}>{name || steamId}</Link>
      </div>
      <div className={styles.calloutHeroScore}>{score}</div>
      <div className={styles.calloutHeroSub}>{sub}</div>
    </div>
  </div>
);

// Best-clip video that autoplays on arrival. Calling play() on mount forces the load too — the
// bare autoPlay attribute gets blocked for unmuted video and won't even preload. Try with sound
// (the user's been clicking, so it's usually granted); if blocked, mute and play so it always runs.
const ClipVideo = ({ clip }: { clip: WrappedClip }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
  }, [clip.id]);
  return (
    <video
      ref={ref}
      className={styles.clipVideo}
      controls
      playsInline
      preload="auto"
      src={clip.clipUrl}
      poster={clip.thumbnailUrl}
    />
  );
};

type SlideDef = {
  key: string;
  theme: string; // CSS class for the gradient wash
  mapBg?: string; // internal map name → photo background
  content: React.ReactNode;
};

// ---------- the page ----------

const WrappedPage = () => {
  const { t, i18n } = useTranslation("pages");
  const { user, isBeta, authLoaded } = useContext(AuthContext);
  const params = useParams<{ season?: string; steamId?: string }>();
  const [wrapped, setWrapped] = useState<WrappedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [clipIdx, setClipIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardIdx, setCardIdx] = useState(0);
  const slideCountRef = useRef(0);
  const completedRef = useRef(false);

  const isMobile = useIsMobile();
  const isAdmin = !!user && admins.includes(user.steamID);
  const steamId = params.steamId ?? user?.steamID;
  // Wrapped reviews a COMPLETED season, so a bare /wrapped must land on WRAPPED_SEASON. It used to
  // fall back to currentSeason, which sent the nudge popup (it links to /wrapped) at a season with
  // no data yet.
  const season = params.season ? parseInt(params.season, 10) : WRAPPED_SEASON;

  useEffect(() => {
    if (!steamId || !season) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setIdx(0);
    setClipIdx(0);
    completedRef.current = false;
    const own = !!user && steamId === user.steamID;
    getWrapped(steamId, season).then((w) => {
      setWrapped(w);
      setLoading(false);
      track("wrapped-open", { season, steamId, own, eligible: w?.eligible ?? false });
    });
    // Server-side view tracking: opening your OWN wrapped satisfies the nudge on every device.
    if (own) trackWrapped(season, "view");
  }, [steamId, season, user]);

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, slideCountRef.current - 1)), []);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const copyLink = useCallback(() => {
    const link = `${window.location.origin}/wrapped/${season}/${steamId}`;
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
    track("wrapped-copy-link", { season });
  }, [season, steamId]);

  const savePng = useCallback(async () => {
    if (!cardRef.current || saving) return;
    setSaving(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2 });
      const a = document.createElement("a");
      a.download = `powerstats-wrapped-s${season}-${wrapped?.playerName ?? steamId}-${cardIdx + 1}.png`;
      a.href = dataUrl;
      a.click();
      track("wrapped-save-card", { season });
    } catch (e) {
      console.error("PNG export failed:", e);
    } finally {
      setSaving(false);
    }
  }, [saving, season, steamId, wrapped?.playerName, cardIdx]);

  // Completion: reaching the last slide of your OWN wrapped marks the funnel + fires an event once.
  const isOwn = !!user && !!steamId && steamId === user.steamID;
  useEffect(() => {
    if (isOwn && slideCountRef.current > 0 && idx >= slideCountRef.current - 1 && !completedRef.current) {
      completedRef.current = true;
      trackWrapped(season, "complete");
      track("wrapped-complete", { season });
    }
  }, [idx, isOwn, season]);

  // Wait for auth regardless of the flag: whose Wrapped this is (isOwn) decides the copy and the
  // completion tracking, so rendering before it resolves flashes the wrong framing.
  if (!authLoaded) return null;
  // WRAPPED_PUBLIC is what opens the page. The beta/admin bypass only existed so previewers could
  // reach it while the flag was false; this check used to ignore the flag entirely, so flipping
  // WRAPPED_PUBLIC did nothing on its own and the page stayed shut to everyone else.
  if (!WRAPPED_PUBLIC && !isBeta && !isAdmin) return <Navigate to="/" replace />;

  // No steamId means a bare /wrapped from someone who is not logged in - almost always a shared
  // link. Bouncing them to the homepage looks like a dead link, so ask them to sign in instead.
  if (!steamId) {
    return (
      <div className={`${styles.stage} ${styles.stageState}`}>
        <div className={styles.stateBox}>
          <p>{t("wrapped.signInPrompt")}</p>
          <button className={styles.actionBtn} onClick={() => { window.location.href = `${baseURL}/Auth/steam`; }}>
            {t("wrapped.signIn")}
          </button>
        </div>
      </div>
    );
  }
  if (loading) return <Loading />;
  if (!wrapped) return (
    <div className={`${styles.stage} ${styles.stageState}`}>
      <div className={styles.stateBox}>{t("wrapped.notFound")}</div>
    </div>
  );

  const w = wrapped;
  const division = fmtDivision(w.division);
  const pctOf = (stat: string) => w.percentiles.find((p) => p.stat === stat);
  const bestMap = w.maps.length > 0 ? w.maps[0] : undefined;
  const worstMap = w.maps.length > 1 ? w.maps[w.maps.length - 1] : undefined;
  const cardBg = bestMap ? mapBackgrounds[bestMap.mapName] : undefined;
  const entryWinPct = w.duels.openingAttempts > 0
    ? Math.round((w.duels.openingWins / w.duels.openingAttempts) * 100)
    : 0;
  const ratingPct = pctOf("rating");
  const adrPct = pctOf("adr");
  const utilPct = pctOf("utilDmg");
  const blindPct = pctOf("timeFlashed");
  const ratingBadge = topPctBadge(t, division && ratingPct?.divisionPercentile != null ? ratingPct.divisionPercentile : ratingPct?.leaguePercentile);
  const adrBadge = topPctBadge(t, adrPct?.leaguePercentile);
  // Par for rating charts/histogram. Rating 3.0 ships raw on HLTV's scale and runs above 1.00
  // on PowerLigaen, so the old hard-coded baseline of 1 mis-drew the "average" line.
  const ratingPar = ratingPct?.divisionMean ?? ratingPct?.leagueMean ?? 1;

  // Share cards: an identity card (archetype + headline stats) and a report card (the new
  // benchmarking, which is the thing worth screenshotting). The report card only exists once
  // percentiles do, so a sub-threshold player still gets a working single-card carousel.
  const cardBenchRows = ([
    { stat: "rating", label: t("wrapped.intro.rating"), fmt: (v: number) => v.toFixed(2) },
    { stat: "adr", label: t("wrapped.intro.adr"), fmt: (v: number) => v.toFixed(1) },
    { stat: "kd", label: "K/D", fmt: (v: number) => v.toFixed(2) },
    { stat: "kast", label: "KAST", fmt: (v: number) => `${v.toFixed(1)}%` },
  ])
    .map((b) => ({ ...b, p: pctOf(b.stat) }))
    .filter((b): b is typeof b & { p: NonNullable<typeof b.p> } => b.p !== undefined)
    .map((b) => ({ ...b, display: b.fmt(b.p.value), inverted: false }));

  const cardVariants: Array<"identity" | "report"> =
    cardBenchRows.length > 0 ? ["identity", "report"] : ["identity"];
  const activeCard = cardVariants[Math.min(cardIdx, cardVariants.length - 1)];
  const nemesis = w.nemesis[0];
  const victim = w.victims[0];
  const clips = (w.bestClips && w.bestClips.length > 0) ? w.bestClips : (w.bestClip ? [w.bestClip] : []);
  const activeClip = clips[Math.min(clipIdx, Math.max(0, clips.length - 1))];

  const dateLocale = i18n.language?.startsWith("en") ? "en-GB" : "da-DK";
  const fmtDate = (iso?: string): string | undefined =>
    iso ? parseMatchTime(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "short" }) : undefined;

  const heroMeta = `${w.playerName ?? ""}${w.teamName ? ` · ${w.teamName}` : ""}${division ? ` · ${division}` : ""}`;

  if (!w.eligible) {
    return (
      <div className={styles.stateBox}>
        <img className={styles.heroPhoto} src={wrappedPhotoUrl(w.steamId, 264)} alt="" />
        <h1 className={styles.title}>{t("wrapped.title", { season: w.season })}</h1>
        <p>{t("wrapped.notEligible", { rounds: w.totals.rounds })}</p>
      </div>
    );
  }

  // ---------------- slides ----------------
  const slides: SlideDef[] = [];

  slides.push({
    key: "intro",
    theme: styles.themeDefault,
    content: (
      <>
        <img className={styles.heroPhoto} src={wrappedPhotoUrl(w.steamId, 380)} alt="" />
        <div className={styles.kicker}>{t("wrapped.kicker")}</div>
        <div className={styles.titleHuge}>{t("wrapped.title", { season: w.season })}</div>
        <p className={styles.lead}>{heroMeta}</p>
      </>
    ),
  });

  slides.push({
    key: "totals",
    theme: styles.themeDefault,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.intro.kicker")}</div>
        <div className={styles.title}>{t("wrapped.intro.title")}</div>
        <Tiles cols={6} mobile={isMobile}>
          <Tile value={w.totals.games} label={t("wrapped.intro.games")} />
          <Tile value={w.totals.rounds} label={t("wrapped.intro.rounds")} />
          <Tile value={w.totals.kills} label={t("wrapped.intro.kills")} />
          <Tile value={w.totals.estimatedHours} decimals={1} label={t("wrapped.intro.hours")} />
          <Tile value={w.totals.rating} decimals={2} label={t("wrapped.intro.rating")} tone="accent" badge={ratingBadge} />
          <Tile value={w.totals.adr} decimals={1} label={t("wrapped.intro.adr")} badge={adrBadge} />
        </Tiles>
        {w.rank && (
          <div className={styles.rankLine}>
            {w.rank.divisionRank != null && division && (
              <span>{t("wrapped.rank.division", { rank: w.rank.divisionRank, pool: w.rank.divisionPool, div: division })}</span>
            )}
            <span>{t("wrapped.rank.league", { rank: w.rank.leagueRank, pool: w.rank.leaguePool })}</span>
            <span>{t("wrapped.rank.seasonHigh", { rating: w.rank.seasonHigh.toFixed(2) })}</span>
          </div>
        )}
        {w.attendance && (
          <p className={styles.disclaimer}>
            {t("wrapped.intro.attendance", { yours: w.attendance.yourGames, team: w.attendance.teamGames })}
          </p>
        )}
      </>
    ),
  });

  // Benchmark slide: every stat graded against the division first, league second — in BOTH
  // directions. This is the "was I actually good?" layer Wrapped was missing.
  if (w.percentiles.length > 0) {
    const bench: Array<{ stat: string; label: string; fmt: (v: number) => string; inverted?: boolean }> = [
      { stat: "rating", label: t("wrapped.intro.rating"), fmt: (v) => v.toFixed(2) },
      { stat: "adr", label: t("wrapped.intro.adr"), fmt: (v) => v.toFixed(1) },
      { stat: "kd", label: "K/D", fmt: (v) => v.toFixed(2) },
      { stat: "kr", label: "K/R", fmt: (v) => v.toFixed(2) },
      { stat: "hsPct", label: t("wrapped.bench.labelHs"), fmt: (v) => `${Math.round(v * 100)}%` },
      { stat: "kast", label: "KAST", fmt: (v) => `${v.toFixed(1)}%` },
      { stat: "utilDmg", label: t("wrapped.blind.utilDamage"), fmt: (v) => v.toFixed(2) },
      { stat: "entryWinPct", label: t("wrapped.duels.winPct"), fmt: (v) => `${Math.round(v * 100)}%` },
      { stat: "timeFlashed", label: t("wrapped.bench.labelBlinded"), fmt: (v) => `${v.toFixed(2)}s`, inverted: true },
    ];
    const rows = bench
      .map((b) => ({ ...b, p: pctOf(b.stat) }))
      .filter((b) => b.p !== undefined);

    if (rows.length > 0) {
      slides.push({
        key: "benchmark",
        theme: styles.themeDefault,
        content: (
          <>
            <div className={styles.kicker}>{t("wrapped.bench.title")}</div>
            <div className={styles.statBars}>
              {rows.map((b) => (
                <StatBar
                  key={b.stat}
                  label={b.label}
                  display={b.fmt(b.p!.value)}
                  pct={b.p!.leaguePercentile}
                  divisionPct={b.p!.divisionPercentile}
                  division={division}
                  inverted={b.inverted}
                />
              ))}
            </div>
          </>
        ),
      });
    }
  }

  // The 3D positional object: kills/deaths/victims/utility/plants as orbitable extruded cells.
  // Needs the wrapped_positions backfill to have run for this season; until then we fall back
  // to the dwell-time area map below.
  if (w.positions && w.positions.maps.length > 0) {
    slides.push({
      key: "positions",
      theme: styles.themeDefault,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.positions.kicker")}</div>
          {/* The title lives inside WrappedPositionMap so it tracks the selected map — built
              here it would be stuck on maps[0] while the switcher changed the render.
              data-no-advance: this slide is a tool, not a page. The filter rows reflow as chips
              appear/disappear, so a click that just misses a chip would otherwise skip the
              slide out from under you. Arrows and the keyboard still navigate. */}
          <div data-no-advance>
            <WrappedPositionMap positions={w.positions} />
          </div>
        </>
      ),
    });
  }

  // Fallback "where you lived" dwell map — only when the 3D object has no data yet.
  if (!(w.positions && w.positions.maps.length > 0) && w.areaDensity && w.areaDensity.areas.length > 0) {
    slides.push({
      key: "areaMap",
      theme: styles.themeDefault,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.areaMap.kicker")}</div>
          <div className={styles.title}>{t("wrapped.areaMap.title", { map: fmtMap(w.areaDensity.mapName) })}</div>
          <WrappedAreaMap density={w.areaDensity} />
        </>
      ),
    });
  }

  if (w.bestGames.length > 0) {
    slides.push({
      key: "bestGames",
      theme: styles.themeDefault,
      mapBg: w.bestGames[0].mapName,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.bestGames.title")}</div>
          <div className={styles.bestGames}>
            {w.bestGames.map((g) => (
              <Link key={g.gameId} className={styles.bestGame} to={`/match/${g.matchId}`}>
                <div className={styles.bestGameTop}>
                  <span className={`${styles.bestGameRating} ${g.won ? styles.bestGameWin : styles.bestGameLoss}`}>
                    {g.rating.toFixed(2)}
                  </span>
                  <span className={`${styles.bestGameResult} ${g.won ? styles.bestGameResultWin : styles.bestGameResultLoss}`}>
                    {g.won ? t("wrapped.chart.win") : t("wrapped.chart.loss")}
                  </span>
                </div>
                <div className={styles.bestGameLine}>
                  <span><b>{g.kills}</b>/{g.deaths}/{g.assists}</span>
                  <span><b>{g.adr.toFixed(1)}</b> ADR</span>
                </div>
                <div className={styles.bestGameMeta}>
                  {fmtMap(g.mapName)}
                  {g.opponentTeamName ? ` · vs ${g.opponentTeamName}` : ""}
                  {fmtDate(g.matchTime) ? ` · ${fmtDate(g.matchTime)}` : ""}
                </div>
              </Link>
            ))}
          </div>
        </>
      ),
    });
  }

  slides.push({
    key: "journey",
    theme: styles.themeDefault,
    mapBg: w.bestGame?.mapName,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.journey.kicker")}</div>
        <div className={styles.title}>{t("wrapped.journey.title")}</div>
        {w.bestGame && (
          <p className={styles.lead}>
            {t("wrapped.journey.best", {
              rating: w.bestGame.rating.toFixed(2),
              opponent: w.bestGame.opponentTeamName ?? "?",
              map: fmtMap(w.bestGame.mapName),
            })}
          </p>
        )}
        <WrappedChart
          color="#f15c25"
          baseline={ratingPar}
          decimals={2}
          highlightIndex={w.bestGame ? w.ratingJourney.findIndex((p) => p.gameId === w.bestGame!.gameId) : undefined}
          points={w.ratingJourney.map((p) => ({
            value: p.rating,
            won: p.won,
            dateLabel: fmtDate(p.matchTime),
            title: `vs ${p.opponentTeamName ?? "?"}`,
            sub: `${fmtMap(p.mapName)} · ${p.won ? t("wrapped.chart.win") : t("wrapped.chart.loss")}`,
          }))}
        />
        <p className={styles.disclaimer}>
          {t("wrapped.journey.streaks", { win: w.longestWinStreak, loss: w.longestLossStreak })}
        </p>
        {w.bestGame && (
          <Link className={styles.matchLink} to={`/match/${w.bestGame.matchId}`}>{t("wrapped.viewMatch")}</Link>
        )}
      </>
    ),
  });

  if (bestMap) {
    slides.push({
      key: "maps",
      theme: styles.themeDefault,
      mapBg: bestMap.mapName,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.maps.kicker")}</div>
          <div className={styles.title}>{t("wrapped.maps.title")}</div>
          {worstMap && worstMap.rating < bestMap.rating && (
            <p className={styles.lead}>
              {t("wrapped.maps.roast", {
                best: fmtMap(bestMap.mapName), bestRating: bestMap.rating.toFixed(2),
                worst: fmtMap(worstMap.mapName), worstRating: worstMap.rating.toFixed(2),
              })}
            </p>
          )}
          <div className={styles.bars}>
            {w.maps.map((m, i) => (
              <Bar key={m.mapName} label={fmtMap(m.mapName)} pct={Math.min(1, m.rating / 1.5)} value={m.rating.toFixed(2)} delay={i * 90} />
            ))}
          </div>
        </>
      ),
    });
  }

  if (w.roles?.dominantCategory) {
    const entries = Object.entries(w.roles.distribution).sort(([, a], [, b]) => b - a);
    slides.push({
      key: "roles",
      theme: styles.themePurple,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.roles.kicker")}</div>
          <div className={styles.title}>{t("wrapped.roles.title")}</div>
          <p className={styles.lead}>
            {t("wrapped.roles.dominant", {
              role: t(`wrapped.roleNames.${w.roles.dominantCategory}`),
              pct: Math.round((w.roles.distribution[w.roles.dominantCategory] ?? 0) * 100),
            })}
          </p>
          <div className={styles.bars}>
            {entries.map(([role, share], i) => (
              <Bar key={role} label={t(`wrapped.roleNames.${role}`)} pct={share} value={`${Math.round(share * 100)}%`} delay={i * 90} />
            ))}
          </div>
          <p className={styles.disclaimer}>{t("wrapped.roles.disclaimer", { rounds: w.roles.roundsTracked })}</p>
        </>
      ),
    });
  }

  if (w.homeArea) {
    slides.push({
      key: "home",
      theme: styles.themeDefault,
      mapBg: w.homeArea.mapName,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.home.kicker")}</div>
          <div className={styles.titleHuge}>{w.homeArea.areaName}</div>
          <p className={styles.lead}>
            {t("wrapped.home.line", {
              minutes: minutes(w.homeArea.totalSeconds),
              area: w.homeArea.areaName,
              map: fmtMap(w.homeArea.mapName),
            })}
          </p>
        </>
      ),
    });
  }

  slides.push({
    key: "clutch",
    theme: styles.themeGold,
    mapBg: w.clutches.biggest?.mapName,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.clutch.kicker")}</div>
        <div className={styles.title}>{t("wrapped.clutch.title")}</div>
        <p className={styles.lead}>{t("wrapped.clutch.total", { wins: w.clutches.totalWins, attempts: w.clutches.totalAttempts })}</p>
        <Tiles cols={5} mobile={isMobile}>
          <Tile value={`${w.clutches.wins1v1}/${w.clutches.attempts1v1}`} label="1v1" />
          <Tile value={`${w.clutches.wins1v2}/${w.clutches.attempts1v2}`} label="1v2" />
          <Tile value={`${w.clutches.wins1v3}/${w.clutches.attempts1v3}`} label="1v3" />
          <Tile value={`${w.clutches.wins1v4}/${w.clutches.attempts1v4}`} label="1v4" />
          <Tile value={`${w.clutches.wins1v5}/${w.clutches.attempts1v5}`} label="1v5" />
        </Tiles>
        {w.clutches.biggest && (
          <>
            <p className={styles.disclaimer}>
              {t("wrapped.clutch.biggest", {
                count: w.clutches.biggest.enemyCount,
                opponent: w.clutches.biggest.opponentTeamName ?? "?",
                map: fmtMap(w.clutches.biggest.mapName),
              })}
            </p>
            <Link className={styles.matchLink} to={`/match/${w.clutches.biggest.matchId}`}>{t("wrapped.viewMatch")}</Link>
          </>
        )}
      </>
    ),
  });

  slides.push({
    key: "duels",
    theme: styles.themeRed,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.duels.kicker")}</div>
        <div className={styles.title}>{t("wrapped.duels.title")}</div>
        <p className={styles.lead}>{t("wrapped.duels.lead", { attempts: w.duels.openingAttempts, pct: entryWinPct })}</p>
        <Tiles cols={3} mobile={isMobile}>
          <Tile value={w.duels.firstKills} label={t("wrapped.duels.firstKills")} tone="good" />
          <Tile value={w.duels.firstDeaths} label={t("wrapped.duels.firstDeaths")} tone="bad" />
          <Tile value={entryWinPct} suffix="%" label={t("wrapped.duels.winPct")} tone="accent" />
        </Tiles>
      </>
    ),
  });

  slides.push({
    key: "multi",
    theme: styles.themeDefault,
    mapBg: w.multikills.aceGame?.mapName,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.multi.kicker")}</div>
        <div className={styles.title}>{t("wrapped.multi.title")}</div>
        <Tiles cols={4} mobile={isMobile}>
          <Tile value={w.multikills.twoK} label="2K" />
          <Tile value={w.multikills.threeK} label="3K" />
          <Tile value={w.multikills.fourK} label="4K" />
          <Tile value={w.multikills.aces} label="ACE" tone="accent" />
        </Tiles>
        {w.multikills.aceGame && (
          <>
            <p className={styles.disclaimer}>
              {t("wrapped.multi.ace", {
                opponent: w.multikills.aceGame.opponentTeamName ?? "?",
                map: fmtMap(w.multikills.aceGame.mapName),
              })}
            </p>
            <Link className={styles.matchLink} to={`/match/${w.multikills.aceGame.matchId}`}>{t("wrapped.viewMatch")}</Link>
          </>
        )}
      </>
    ),
  });

  if (nemesis) {
    slides.push({
      key: "nemesis",
      theme: styles.themeRed,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.nemesis.kicker")}</div>
          <div className={styles.title}>{t("wrapped.nemesis.title")}</div>
          <p className={styles.lead}>{t("wrapped.nemesis.lead")}</p>
          <PlayerHero
            steamId={nemesis.steamId}
            name={nemesis.playerName}
            score={`${nemesis.killsOnYou}–${nemesis.yourKillsOnThem}`}
            sub={t("wrapped.nemesis.sub", { kills: nemesis.killsOnYou })}
          />
          {w.nemesis.length > 1 && (
            <div className={styles.calloutMinis}>
              {w.nemesis.slice(1).map((n) => (
                <div key={n.steamId} className={styles.calloutMini}>
                  <img src={wrappedPhotoUrl(n.steamId, 72)} alt="" />
                  <Link to={`/player/${n.steamId}`}>{n.playerName || n.steamId}</Link>
                  <span className={styles.calloutMiniScore}>{n.killsOnYou}–{n.yourKillsOnThem}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ),
    });
  }

  if (victim) {
    slides.push({
      key: "victims",
      theme: styles.themeGreen,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.victims.kicker")}</div>
          <div className={styles.title}>{t("wrapped.victims.title")}</div>
          <p className={styles.lead}>{t("wrapped.victims.lead")}</p>
          <PlayerHero
            steamId={victim.steamId}
            name={victim.playerName}
            score={`${victim.yourKillsOnThem}–${victim.killsOnYou}`}
            sub={t("wrapped.victims.sub", { kills: victim.yourKillsOnThem })}
            good
          />
          {w.victims.length > 1 && (
            <div className={styles.calloutMinis}>
              {w.victims.slice(1).map((v) => (
                <div key={v.steamId} className={styles.calloutMini}>
                  <img src={wrappedPhotoUrl(v.steamId, 72)} alt="" />
                  <Link to={`/player/${v.steamId}`}>{v.playerName || v.steamId}</Link>
                  <span className={styles.calloutMiniScore}>{v.yourKillsOnThem}–{v.killsOnYou}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ),
    });
  }

  slides.push({
    key: "blind",
    theme: styles.themeFlash,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.blind.kicker")}</div>
        <div className={styles.title}>{t("wrapped.blind.title")}</div>
        <p className={styles.lead}>
          {t("wrapped.blind.line", { minutes: minutes(w.flash.timeFlashedSeconds) })}
          {blindPct && blindPct.leaguePercentile > 0.5 && (
            <> {t("wrapped.blind.roast", { pct: morePct(blindPct.leaguePercentile) })}</>
          )}
        </p>
        <Tiles cols={3} mobile={isMobile}>
          <Tile value={minutes(w.flash.enemyTimeFlashedSeconds)} label={t("wrapped.blind.given")} />
          <Tile value={w.flash.enemiesFlashed} label={t("wrapped.blind.enemiesFlashed")} />
          <Tile value={Math.round(w.flash.utilityDamage)} label={t("wrapped.blind.utilDamage")} badge={topPctBadge(t, utilPct?.leaguePercentile)} />
        </Tiles>
      </>
    ),
  });

  if (w.elo) {
    slides.push({
      key: "elo",
      theme: styles.themeBlue,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.elo.kicker")}</div>
          <div className={styles.title}>{t("wrapped.elo.title", { nickname: w.faceit?.nickname ?? w.playerName ?? "" })}</div>
          <p className={styles.lead}>
            {t(w.elo.net >= 0 ? "wrapped.elo.leadUp" : "wrapped.elo.leadDown", { start: w.elo.start, end: w.elo.end, net: Math.abs(w.elo.net) })}
          </p>
          <WrappedChart
            color="#60a5fa"
            decimals={0}
            points={w.elo.points.map((p) => ({ value: p.elo, dateLabel: fmtDate(p.date), title: fmtDate(p.date) }))}
          />
          <Tiles cols={5} mobile={isMobile}>
            <Tile value={w.elo.high} label={t("wrapped.elo.high")} tone="accent" />
            <Tile value={w.elo.low} label={t("wrapped.elo.low")} />
            <Tile value={w.elo.gained} label={t("wrapped.elo.gained")} tone="good" />
            <Tile value={w.elo.lost} label={t("wrapped.elo.lost")} tone="bad" />
            <Tile value={`${w.elo.net >= 0 ? "+" : ""}${w.elo.net}`} label={t("wrapped.elo.net")} tone={w.elo.net >= 0 ? "good" : "bad"} />
          </Tiles>
        </>
      ),
    });
  }

  // ---- data pack (trades / friendly flashes / team kills / style kills) ----
  // Only present for seasons Wrapped is offered for, all fully data-pack-parsed, so the numbers
  // are always a complete season — no coverage caveat needed.
  const pack = w.dataPack;

  if (pack) {
    const bodyguard = pack.bodyguards[0];
    if (bodyguard) {
      slides.push({
        key: "bodyguard",
        theme: styles.themeGreen,
        content: (
          <>
            <div className={styles.kicker}>{t("wrapped.bodyguard.kicker")}</div>
            <div className={styles.title}>{t("wrapped.bodyguard.title")}</div>
            <p className={styles.lead}>{t("wrapped.bodyguard.lead")}</p>
            <PlayerHero
              steamId={bodyguard.steamId}
              name={bodyguard.playerName}
              score={`${bodyguard.count}`}
              sub={t("wrapped.bodyguard.sub", { count: bodyguard.count })}
              good
            />
            {/* Reciprocal direction, in its own divided + titled block so it's unmistakably a
                different stat from the hero: hero = who avenged you, this = who you avenged. */}
            {pack.saved.length > 0 && (
              <div className={styles.recip}>
                <div className={styles.recipHeader}>{t("wrapped.bodyguard.savedKicker")}</div>
                <div className={styles.recipSub}>{t("wrapped.bodyguard.savedTitle")}</div>
                <div className={styles.calloutMinis}>
                  {pack.saved.slice(0, 4).map((s) => (
                    <div key={s.steamId} className={styles.calloutMini}>
                      <img src={wrappedPhotoUrl(s.steamId, 72)} alt="" />
                      <Link to={`/player/${s.steamId}`}>{s.playerName || s.steamId}</Link>
                      <span className={styles.calloutMiniScore}>{t("wrapped.bodyguard.savedSub", { count: s.count })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ),
      });
    }

    if (pack.teammatesFlashed > 0) {
      slides.push({
        key: "flasher",
        theme: styles.themeDefault,
        content: (
          <>
            <div className={styles.kicker}>{t("wrapped.flasher.kicker")}</div>
            <div className={styles.title}>{t("wrapped.flasher.title", { count: pack.teammatesFlashed })}</div>
            <p className={styles.lead}>
              {t("wrapped.flasher.lead", { seconds: Math.round(pack.teammateFlashDuration) })}
            </p>
            {pack.blinded.length > 0 && (
              <>
                <p className={styles.leadMuted}>{t("wrapped.flasher.worst")}</p>
                <div className={styles.calloutMinis}>
                  {pack.blinded.slice(0, 4).map((b) => (
                    <div key={b.steamId} className={styles.calloutMini}>
                      <img src={wrappedPhotoUrl(b.steamId, 72)} alt="" />
                      <Link to={`/player/${b.steamId}`}>{b.playerName || b.steamId}</Link>
                      <span className={styles.calloutMiniScore}>{t("wrapped.flasher.sub", { count: b.count })}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        ),
      });
    }

    if (pack.teamKills > 0) {
      slides.push({
        key: "teamkills",
        theme: styles.themeRed,
        content: (
          <>
            <div className={styles.kicker}>{t("wrapped.teamkills.kicker")}</div>
            <div className={styles.title}>
              {t(pack.teamKills === 1 ? "wrapped.teamkills.titleOne" : "wrapped.teamkills.title", { count: pack.teamKills })}
            </div>
            <p className={styles.lead}>{t("wrapped.teamkills.lead")}</p>
          </>
        ),
      });
    }

    const styleKills = pack.wallbangKills + pack.noscopeKills + pack.smokeKills + pack.blindKills;
    if (styleKills > 0) {
      slides.push({
        key: "pizzazz",
        theme: styles.themePurple,
        content: (
          <>
            <div className={styles.kicker}>{t("wrapped.pizzazz.kicker")}</div>
            <div className={styles.title}>{t("wrapped.pizzazz.title")}</div>
            <Tiles cols={4} mobile={isMobile}>
              <Tile value={pack.wallbangKills} label={t("wrapped.pizzazz.wallbang")} tone="accent" />
              <Tile value={pack.noscopeKills} label={t("wrapped.pizzazz.noscope")} />
              <Tile value={pack.smokeKills} label={t("wrapped.pizzazz.smoke")} />
              <Tile value={pack.blindKills} label={t("wrapped.pizzazz.blind")} />
            </Tiles>
          </>
        ),
      });
    }
  }

  if (w.halves) {
    slides.push({
      key: "halves",
      theme: styles.themeDefault,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.halves.kicker")}</div>
          <div className={styles.title}>{t("wrapped.halves.title")}</div>
          <Tiles cols={2} mobile={isMobile}>
            <Tile value={w.halves.firstHalf.rating} decimals={2} label={t("wrapped.halves.first", { games: w.halves.firstHalf.games })} />
            <Tile
              value={w.halves.secondHalf.rating}
              decimals={2}
              label={t("wrapped.halves.second", { games: w.halves.secondHalf.games })}
              tone={w.halves.secondHalf.rating > w.halves.firstHalf.rating ? "good" : "bad"}
            />
          </Tiles>
          <p className={styles.lead}>
            {w.halves.secondHalf.rating > w.halves.firstHalf.rating
              ? t("wrapped.halves.improved")
              : t("wrapped.halves.declined")}
          </p>
        </>
      ),
    });
  }

  if (w.transfer) {
    slides.push({
      key: "transfer",
      theme: styles.themePurple,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.transfer.kicker")}</div>
          <div className={styles.title}>{t("wrapped.transfer.title")}</div>
          <p className={styles.lead}>
            {t("wrapped.transfer.line", { from: w.transfer.fromTeamName ?? "?", to: w.transfer.toTeamName ?? "?" })}
          </p>
        </>
      ),
    });
  }

  if (activeClip) {
    slides.push({
      key: "clip",
      theme: styles.themeDefault,
      mapBg: activeClip.mapName,
      content: (
        <>
          <div className={styles.kicker}>{t("wrapped.clip.kicker")}</div>
          <div className={styles.title}>{t("wrapped.clip.title")}</div>
          <p className={styles.lead}>{t("wrapped.clip.lead", { kills: activeClip.killCount, map: fmtMap(activeClip.mapName) })}</p>
          <ClipVideo key={activeClip.id} clip={activeClip} />
          {clips.length > 1 && (
            <div className={styles.clipThumbs}>
              {clips.map((c, i) => (
                <button
                  key={c.id}
                  className={`${styles.clipThumb} ${i === Math.min(clipIdx, clips.length - 1) ? styles.clipThumbActive : ""}`}
                  onClick={(e) => { e.stopPropagation(); setClipIdx(i); }}
                >
                  {c.thumbnailUrl && <img src={c.thumbnailUrl} alt="" loading="lazy" />}
                  <div className={styles.clipThumbLabel}>#{i + 1} · {c.killCount}K · {fmtMap(c.mapName)}</div>
                </button>
              ))}
            </div>
          )}
        </>
      ),
    });
  }

  slides.push({
    key: "archetype",
    theme: styles.themePurple,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.archetype.kicker")}</div>
        <div className={styles.titleHuge}>{t(`wrapped.archetypes.${w.archetype}`)}</div>
        <p className={styles.lead}>{t(`wrapped.archetypeFlavor.${w.archetype}`)}</p>
      </>
    ),
  });

  slides.push({
    key: "tldr",
    theme: styles.themeDefault,
    content: (
      <>
        <div className={styles.kicker}>{t("wrapped.tldr.kicker")}</div>
        <div className={styles.title}>{t("wrapped.tldr.title")}</div>
        <p className={`${styles.lead} ${styles.leadMuted}`}>{t("wrapped.tldr.lead")}</p>
        {!WRAPPED_PUBLIC && <div style={{ marginBottom: "0.9rem" }}><BetaBanner /></div>}
        <div className={styles.tldrActions}>
          <button className={styles.actionBtn} onClick={savePng} disabled={saving}>
            {saving ? t("wrapped.tldr.saving") : t("wrapped.tldr.save")}
          </button>
          <button className={`${styles.actionBtn} ${styles.actionBtnGhost}`} onClick={copyLink}>
            {copied ? t("wrapped.tldr.copied") : t("wrapped.tldr.copy")}
          </button>
        </div>
        {/* Retention loop, straight from FACEIT's hub: the recap is the hook into what's next. */}
        {season + 1 === currentSeason && (
          <Link
            className={styles.nextSeason}
            to="/fantasy"
            onClick={() => track("wrapped-next-season", { season })}
          >
            {t("wrapped.tldr.nextSeason", { season: season + 1 })}
          </Link>
        )}
        {cardVariants.length > 1 && (
          <div className={styles.cardNav}>
            <button
              className={styles.cardNavBtn}
              onClick={() => setCardIdx((i) => (i - 1 + cardVariants.length) % cardVariants.length)}
              aria-label={t("wrapped.tldr.prevCard")}
            >‹</button>
            <span className={styles.cardNavCount}>{cardIdx + 1} / {cardVariants.length}</span>
            <button
              className={styles.cardNavBtn}
              onClick={() => setCardIdx((i) => (i + 1) % cardVariants.length)}
              aria-label={t("wrapped.tldr.nextCard")}
            >›</button>
          </div>
        )}
        <div ref={cardRef} className={styles.seasonCard}>
          {cardBg
            ? <div className={styles.seasonCardBg} style={{ backgroundImage: `url(${cardBg})` }} />
            : <div className={`${styles.seasonCardBg} ${styles.themePurple}`} />}
          <div className={styles.seasonCardWash} />
          <div className={styles.seasonCardInner}>
            {/* Header strip — FACEIT puts the season brand left, the share URL right. */}
            <div className={styles.cardHead}>
              <span className={styles.cardHeadBrand}>
                {t("wrapped.kicker")} <b>{t("wrapped.title", { season: w.season })}</b>
              </span>
              <span className={styles.cardHeadUrl}>powerstats.dk/wrapped</span>
            </div>

            <div className={styles.cardBody}>
              {/* Identity column: photo, name, hero rating, placement. */}
              <div className={styles.cardIdentity}>
                <img className={styles.cardPhoto} src={wrappedPhotoUrl(w.steamId, 256)} alt="" crossOrigin="anonymous" />
                <div className={styles.cardName}>{w.playerName}</div>
                <div className={styles.cardMeta}>
                  {w.teamName}{division ? ` · ${division}` : ""}
                </div>
                <div className={styles.cardHero}>
                  <span className={styles.cardHeroValue}>{w.totals.rating.toFixed(2)}</span>
                  <span className={styles.cardHeroLabel}>{t("wrapped.intro.rating")}</span>
                </div>
                {w.rank && (
                  <div className={styles.cardRankLine}>
                    {w.rank.divisionRank != null && division
                      ? t("wrapped.rank.division", { rank: w.rank.divisionRank, pool: w.rank.divisionPool, div: division })
                      : t("wrapped.rank.league", { rank: w.rank.leagueRank, pool: w.rank.leaguePool })}
                  </div>
                )}
                <div className={styles.cardArchetypeTag}>{t(`wrapped.archetypes.${w.archetype}`)}</div>
              </div>

              {/* Right column: tile grid (identity card) or benchmark bars (report card). */}
              {activeCard === "identity" ? (
                <div className={styles.cardTiles}>
                  <CardTile value={w.totals.adr.toFixed(0)} label="ADR" />
                  <CardTile value={w.totals.kills} label={t("wrapped.intro.kills")} />
                  <CardTile value={`${Math.round(w.totals.kast)}%`} label="KAST" />
                  <CardTile value={w.clutches.totalWins} label={t("wrapped.tldr.clutches")} />
                  <CardTile value={w.multikills.aces} label="ACES" />
                  {w.elo
                    ? <CardTile value={`${w.elo.net >= 0 ? "+" : ""}${w.elo.net}`} label="ELO" tone={w.elo.net >= 0 ? "good" : "bad"} />
                    : <CardTile value={w.rank ? w.rank.seasonHigh.toFixed(2) : w.totals.adr.toFixed(0)} label={t("wrapped.tldr.best")} />}
                </div>
              ) : (
                <div className={styles.cardReport}>
                  <div className={styles.cardReportTitle}>{t("wrapped.bench.title")}</div>
                  <div className={styles.cardBars}>
                    {cardBenchRows.map((b) => (
                      <CardBar
                        key={b.stat}
                        label={b.label}
                        display={b.display}
                        pct={b.p.leaguePercentile}
                        divisionPct={b.p.divisionPercentile}
                        inverted={b.inverted}
                      />
                    ))}
                  </div>
                  {w.rank && (
                    <div className={styles.cardReportFoot}>
                      {t("wrapped.rank.seasonHigh", { rating: w.rank.seasonHigh.toFixed(2) })}
                      {bestMap ? ` · ${fmtMap(bestMap.mapName)}` : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    ),
  });

  slideCountRef.current = slides.length;
  const current = slides[Math.min(idx, slides.length - 1)];
  const mapBgUrl = current.mapBg ? mapBackgrounds[current.mapBg] : undefined;

  // Clicks advance the show — unless they hit something interactive.
  const onStageClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // canvas is draggable (the 3D map): releasing an orbit drag must not advance the slide.
    if (target.closest("a,button,video,canvas,[data-no-advance]")) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    if (e.clientX - rect.left < rect.width * 0.25) prev(); else next();
  };

  return (
    <div className={styles.stage} onClick={onStageClick}>
      {mapBgUrl
        ? <><div className={styles.bg} style={{ backgroundImage: `url(${mapBgUrl})` }} /><div className={styles.bgOverlay} /></>
        : <><div className={`${styles.bg} ${current.theme}`} style={{ animation: "none", transform: "none" }} /><div className={styles.vignette} /></>}

      <div className={styles.progress}>
        {slides.map((s, i) => (
          <div
            key={s.key}
            className={`${styles.progressSeg} ${i < idx ? styles.progressSegDone : i === idx ? styles.progressSegActive : ""}`}
          />
        ))}
      </div>
      <div className={styles.counter}>{idx + 1} / {slides.length}</div>
      {!WRAPPED_PUBLIC && <div className={styles.betaTag}>{t("wrapped.betaTag")}</div>}

      <div key={current.key} className={styles.slide} data-slide={current.key}>
        {current.content}
      </div>

      {idx > 0 && (
        <button className={`${styles.chevron} ${styles.chevronLeft}`} onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="prev">‹</button>
      )}
      {idx < slides.length - 1 && (
        <button className={`${styles.chevron} ${styles.chevronRight}`} onClick={(e) => { e.stopPropagation(); next(); }} aria-label="next">›</button>
      )}

      {idx === 0 && <div className={styles.hint}>{t("wrapped.nav.hint")}</div>}

      {/* Mobile bottom nav — hidden on desktop (chevrons take over there). Gives phones a
          discoverable, thumb-reachable prev / next / share instead of relying on tap zones. */}
      <div className={styles.bottomBar} onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.bottomBarIcon}
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label={t("wrapped.nav.prev")}
          disabled={idx === 0}
        >‹</button>
        <button
          className={styles.bottomBarNext}
          onClick={(e) => { e.stopPropagation(); next(); }}
          disabled={idx >= slides.length - 1}
        >
          {idx >= slides.length - 1 ? t("wrapped.nav.done") : t("wrapped.nav.next")}
          {idx < slides.length - 1 && <span aria-hidden>→</span>}
        </button>
        <button
          className={styles.bottomBarIcon}
          onClick={(e) => { e.stopPropagation(); copyLink(); }}
          aria-label={t("wrapped.nav.share")}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8h16v-8" /><path d="M12 3v13" /><path d="M8 7l4-4 4 4" />
          </svg>
        </button>
      </div>

      {copied && <div className={styles.toast}>{t("wrapped.tldr.copied")}</div>}
    </div>
  );
};

export default WrappedPage;
